import {
  Stack,
  Paper,
  InputWrapper,
  Modal,
  TextInput,
  Title,
  Text,
} from "@mantine/core"
import { useField } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { useEffect } from "react"

import { TreeNodeType } from "@costory/types/endpoints/folders"
import { ComponentType, ExplorerChartType } from "@costory/types/prisma-client"

import { FileExplorer } from "@costory/front/components/FileExplorer/FileExplorer"
import { FilterBar } from "@costory/front/components/FilterBar"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  ChartContext,
  useChartContext,
} from "@costory/front/contexts/ChartContext"
import { ExplorerMainChart } from "@costory/front/pages/Explorer/ExplorerMainChart"
import { useAxesQuery } from "@costory/front/queries/axes"
import { useBusinessMetricsQuery } from "@costory/front/queries/businessMetrics"
import { useCreateSavedViewMutation } from "@costory/front/queries/savedViews"

type Props = {
  onSaveView: (savedViewId: string, savedViewName: string) => void
  onResetFilters?: (resetFn: () => void) => void
}

const _NewSavedView = ({ onSaveView, onResetFilters }: Props) => {
  const [showComparisonChart, { toggle: toggleShowComparisonChart }] =
    useDisclosure(false)

  const {
    metricsOptions,
    groupByOptions,
    metrics,
    filterForm,
    referenceFilters,
    setExplorerChartType,
    filters,
    drillDownInto,
  } = useChartContext()
  useEffect(() => {
    if (referenceFilters) {
      const resetFn = () => {
        filterForm.setValues(referenceFilters)
      }
      onResetFilters?.(resetFn)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedMetric = metrics.find(
    (m) => m.id === filterForm.getValues().metricId,
  )

  const [isOpen, { close: closeSaveViewModal, open: openSaveViewModal }] =
    useDisclosure(false)

  const newViewNameField = useField({
    initialValue: "",
  })

  const { mutateAsync: createSavedView, isPending: isCreatingSavedView } =
    useCreateSavedViewMutation()

  const handleOpenFolder = async (folderId: string) => {
    try {
      const savedView = await createSavedView({
        name: newViewNameField.getValue(),
        type: ComponentType.PUBLIC,
        parentFolderId: folderId,
        ...filterForm.getValues(),
      })
      filterForm.setValues(referenceFilters)
      closeSaveViewModal()
      onSaveView(savedView.id, savedView.name)
    } catch (e) {
      console.error(e)
    }
  }
  return (
    <>
      <Stack>
        <FilterBar
          form={filterForm}
          metricsOptions={metricsOptions}
          groupByOptions={groupByOptions}
          queryError={false}
          isNotOtherNode={true}
          loading={false}
          showComparisonChart={showComparisonChart}
          canToggleWhereClause
          toggleShowComparisonChart={
            filters.explorerChartType !== ExplorerChartType.WATERFALL
              ? toggleShowComparisonChart
              : undefined
          }
          openSaveModal={openSaveViewModal}
          defaultShowWhereClause
        />
        <Paper flex={1} px={32} py={24}>
          <Stack h={400} justify="center">
            <ExplorerMainChart
              drillDownInto={drillDownInto}
              explorerChartType={filters.explorerChartType}
              filters={filters}
              setExplorerChartType={setExplorerChartType}
              selectedMetric={selectedMetric}
              showComparisonChart={showComparisonChart}
            />
          </Stack>
        </Paper>
      </Stack>
      <Modal
        size="80vw"
        opened={isOpen}
        onClose={closeSaveViewModal}
        title={<Title order={2}>Save view</Title>}
        styles={{ body: { height: "70vh" } }}
      >
        <Stack align="stretch" h="90%">
          <InputWrapper>
            <Text fw="bold">1. Choose a view name</Text>
            <TextInput
              {...newViewNameField.getInputProps()}
              flex={1}
              placeholder="Production cost breakdown"
            />
          </InputWrapper>
          <Text fw="bold">2. Select a folder</Text>
          <Stack h="90%">
            <FileExplorer
              onOpenFolder={handleOpenFolder}
              buttonProps={{
                label: "Save",
                loading: isCreatingSavedView,
                disabled: !newViewNameField.getValue().trim(),
                disabledTooltip: "Choose a name for your view to save it",
              }}
              onDoubleClick={(id, type) => {
                if (type !== TreeNodeType.Folder) {
                  return
                }

                handleOpenFolder(id)
              }}
              disableHighlightStyle={true}
              isInSaveMode
            />
          </Stack>
        </Stack>
      </Modal>
    </>
  )
}

export const NewSavedView = ({ onSaveView, onResetFilters }: Props) => {
  const queryMetrics = useBusinessMetricsQuery()
  const queryAxes = useAxesQuery()

  return (
    <QueryWrapper query={queryAxes}>
      {({ data: axes }) => (
        <QueryWrapper query={queryMetrics}>
          {({ data: metrics }) => (
            <ChartContext.Provider
              value={{
                axes,
                metrics,
                redirectPage: "explorer",
              }}
            >
              <_NewSavedView
                onResetFilters={onResetFilters}
                onSaveView={onSaveView}
              />
            </ChartContext.Provider>
          )}
        </QueryWrapper>
      )}
    </QueryWrapper>
  )
}
